/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import {
  type CalendarEvent,
  type DisplayedTranscriptOutput,
  type FormattedTask,
  type Transcript,
  type TranscriptFull,
  type TranscriptOutput,
  type TranscriptStatus,
  type TranscriptSummary,
  MissingTranscriptError
} from '@repo/common';
import type { Child } from '@repo/common/types/Process.d';
import { timeBetween } from '@repo/toolslib/src/utils/date.util';
import { debounceAsync } from '@repo/toolslib/src/utils/debounce.util';
import { uuidV4 } from '@repo/toolslib/src/utils/id.util';
import {
  demoteFullTranscriptToSummary,
  isMediaFileLike,
  isStoredMediaFileLike,
  isTranscriptFull,
  isTranscriptSummary
} from '@repo/toolslib/src/utils/transcript.util';
import { LRUCache } from 'lru-cache';
import type { Readable, Writable } from 'svelte/store';
import { derived, get, writable } from 'svelte/store';
import { MediaFile } from '../models/mediaFile';
import { getHandlerAsync as _getHandlerAsync } from '../sharedlib';
import { viewer } from './viewer';

let _handler: SharedLibHandler | undefined;

const getHandlerAsync = async (): Promise<SharedLibHandler> => {
  if (_handler) {
    return _handler;
  }
  _handler = await _getHandlerAsync();
  return _handler;
};

/**
 * Utility function to safely remove a transcript from the search index
 * This function handles all error cases and doesn't throw exceptions
 */
export const removeFromSearchIndex = async (transcriptId: string, source: string = 'unknown') => {
  try {
    const handler = _handler ?? (await getHandlerAsync());
    await handler.deleteTranscriptFromIndex(transcriptId);
    console.log(`Removed transcript ${transcriptId} from search index (${source})`);
  } catch (error) {
    console.warn(
      `Failed to remove transcript ${transcriptId} from search index (${source}):`,
      error
    );
    // Don't throw - operations should succeed even if search index removal fails
  }
};

/**
 * Utility function to check if a transcript is considered deleted
 * Uses deletedAt as the primary indicator, with isDeleted as fallback
 */
export const isTranscriptDeleted = (transcript: Transcript): boolean => {
  return !!(transcript.deletedAt || transcript.isDeleted);
};

/**
 * Utility function to mark a transcript as deleted
 * Sets both deletedAt and isDeleted for consistency during migration
 */
export const markTranscriptAsDeleted = (transcript: Transcript): void => {
  transcript.deletedAt = new Date();
  transcript.updatedAt = new Date();
  transcript.isDeleted = true;
};

import type { IMediaFile, Meeting, MinutesResponseData, StoredMediaFile } from '@repo/common';
import type { SharedLibHandler } from '@repo/common/types/SharedLib';
import { getTranscriptOutput } from '@repo/toolslib/src/utils/transcript.util';
import { sendToTray } from '../events/tray.event';
import { userAuthToken } from './auth.store';
import { workspaceStore } from './workspace.store';

const FULL_TRANSCRIPT_CACHE_SIZE = 50;
const FULL_TRANSCRIPT_CACHE_TTL_MS = 5 * 60 * 1000;

const fullTranscriptCache = new LRUCache<string, TranscriptFull>({
  max: FULL_TRANSCRIPT_CACHE_SIZE,
  ttl: FULL_TRANSCRIPT_CACHE_TTL_MS
});

export const ensureTranscriptFull = (
  transcript: Transcript | undefined,
  context: string
): TranscriptFull => {
  if (!transcript) {
    throw new Error(`[transcripts] ${context}: transcript is missing`);
  }
  if (isTranscriptFull(transcript)) {
    return transcript;
  }

  // See if it was cached.
  const cached = fullTranscriptCache.get(transcript.id);
  if (cached) {
    return cached;
  }

  throw new Error(
    `[transcripts] ${context} requires a TranscriptFull. Call getFullTranscript("${transcript.id}") first.`
  );
};

const coerceStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value as string[];
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.debug('[transcripts] Failed to parse string array field', error);
      return [];
    }
  }
  return [];
};

const normalizeMediaFile = async (file: unknown): Promise<IMediaFile | null | undefined> => {
  if (!file) {
    return file as null;
  }

  if (isMediaFileLike(file)) {
    return file as IMediaFile;
  }

  const handler = await getHandlerAsync();

  if (typeof file === 'string') {
    try {
      const parsed = JSON.parse(file) as StoredMediaFile;
      return await handler.fromStoredMediaFile(parsed);
    } catch (error) {
      console.debug('[transcripts] Failed to hydrate media file from stored string', error);
      return null;
    }
  }

  if (isStoredMediaFileLike(file)) {
    try {
      return await handler.fromStoredMediaFile(file);
    } catch (error) {
      console.debug('[transcripts] Failed to hydrate media file from stored object', error);
      return null;
    }
  }

  console.debug('[transcripts] Unsupported media file format encountered');
  return null;
};

const hydrateTranscriptFromRecord = async (record: TranscriptFull): Promise<TranscriptFull> => {
  const rawOutput = coerceStringArray(record.rawOutput ?? []);
  const editedOutput = coerceStringArray(record.editedOutput ?? rawOutput);
  const meeting: Meeting | null =
    isTranscriptFull(record) && typeof record.meeting === 'string'
      ? JSON.parse(record.meeting)
      : (record.meeting as Meeting | null);
  const file = await normalizeMediaFile((record as any).file);

  return {
    ...(record as TranscriptFull),
    _type: 'fullTranscript',
    rawOutput,
    editedOutput,
    meeting: meeting,
    file: file ?? null
  };
};

const cacheFullTranscript = (transcript: TranscriptFull): TranscriptFull => {
  fullTranscriptCache.set(transcript.id, transcript);
  return transcript;
};

export type Transcripts = {
  idMap: Map<string, TranscriptSummary>;
  eventMap: Map<string, TranscriptSummary>;
  selectedEventId?: string;
  loaded: boolean;
};

export type TranscriptsView = {
  audioBlobUrls: Map<string, string>;
  active: string | null;
  whisperProcess?: Child;
  modalOpen: boolean;
  loaded: boolean;
};

const initialView = {
  audioBlobUrls: new Map<string, string>(),
  active: null,
  modalOpen: false,
  loaded: false
};

export const transcriptsView = writable<TranscriptsView>(initialView);

const updateTranscript = async (
  id: string,
  partialUpdate: Partial<TranscriptFull>,
  immediate?: boolean,
  doSync?: boolean
): Promise<TranscriptFull | undefined> => {
  let found: TranscriptFull | undefined;
  const full = await getFullTranscript(id);
  transcripts.update((t) => {
    const $transcriptsView = get(transcriptsView);
    if ($transcriptsView.active !== full.id) {
      console.warn(
        `Updating transcript (${full.id}) that is not active (${$transcriptsView.active})`
      );
    }
    console.debug('[updateTranscript] Updating transcript');
    const newData = { ...full, ...partialUpdate } as TranscriptFull;
    // Preserve an existing name if update omits it or sets it undefined/null
    newData.name = newData.name || full.name;
    if (partialUpdate?.rawOutput !== undefined) {
      // Make sure we force refreshing the output before saving it.
      try {
        getTranscriptOutput(newData, true);
      } catch (err) {
        console.warn('Could not update output due to parsing error', err);
      }
    }
    cacheFullTranscript(newData);
    const newIdMap = new Map(t.idMap);
    newIdMap.set(id, demoteFullTranscriptToSummary(newData));
    found = newData;
    console.debug('[updateTranscript] Updated transcript', newData.id);
    return { ...t, idMap: newIdMap };
  });

  if (found) {
    viewer.update(($viewer) => {
      if ($viewer.activeTranscript?.id !== id) {
        return $viewer;
      }
      return {
        ...$viewer,
        activeTranscript: found
      };
    });

    // Send to tray
    await sendToTray({ transcript: found });

    // If transcript is being marked as deleted, remove from search index
    if (partialUpdate.isDeleted === true && !isTranscriptDeleted(found)) {
      removeFromSearchIndex(id, 'updateTranscript').then();
    }

    if (immediate) {
      await saveTranscriptionLocal(found.id, partialUpdate, doSync);
    } else {
      debouncedSaveTranscript(found.id, partialUpdate).then();
    }
  }
  return found;
};

const createFromData = async (
  file?: MediaFile,
  calendarEvent?: CalendarEvent,
  duration?: number,
  status: TranscriptStatus = 'empty',
  workspaceId?: string,
  captureType?: string
) => {
  const handler = _handler ?? (await getHandlerAsync());
  // TODO: Check if file exists before creating
  console.log('Called createFromFile');
  const id = uuidV4();
  const eventId = calendarEvent?.uid || undefined;
  const definedAttendees = calendarEvent?.attendees?.filter((x) => x?.email) || [];

  // Determine the date for this transcript
  const transcriptDate = calendarEvent?.start ? new Date(calendarEvent.start) : new Date();

  const userId = handler.getUserId();
  if (!userId) {
    throw new Error('User ID not set');
  }

  const transcript: TranscriptFull & Record<string, unknown> = {
    _type: 'fullTranscript',
    id,
    file: file,
    title: calendarEvent?.title || file?.fileName || 'New memo',
    name: calendarEvent?.title || file?.fileName || 'New memo',
    status,
    rawOutput: [],
    editedOutput: [],
    data: captureType ? { captureType } : undefined,
    duration: duration || 0,
    eventId,
    fileExists: false,
    workspaceId,
    date: transcriptDate, // Use calendar event date or current time
    createdAt: new Date(),
    updatedAt: new Date(),
    isDeleted: false,
    userId,
    meeting: {
      date: transcriptDate, // Use the same date as the transcript
      attendees: definedAttendees.map((x) => x.email || ''),
      recording: file?.path || '',
      minutes: null
    }
  };

  transcripts.update((t) => {
    t.idMap.set(id, demoteFullTranscriptToSummary(transcript));
    if (transcript.eventId) {
      t.eventMap.set(transcript.eventId, demoteFullTranscriptToSummary(transcript));
    }
    return t;
  });
  cacheFullTranscript(transcript as TranscriptFull);

  const { transcriptModel } = await handler.getDatabaseModels();
  await transcriptModel.bulkCreateItems([transcript]);

  if (duration === undefined || isNaN(duration) || !transcript.fileExists) {
    // Run in the background
    await transcripts.calculateDuration(id).then();
  }
  // Run in the background
  await transcripts.addMeeting(id);

  return transcript;
};

interface TranscriptsWritable extends Writable<Transcripts> {
  deleteFile: (id: string) => Promise<void>;
  createFromData: (
    file?: MediaFile,
    calendarEvent?: CalendarEvent,
    duration?: number,
    status?: TranscriptStatus,
    workspaceId?: string,
    captureType?: string
  ) => Promise<TranscriptFull>;
  removeTranscriptEntry: (id: string) => void;
  setStatus: (id: string, status: Transcript['status']) => void;
  setName: (id: string, name: string) => void;
  addEventId: (id: string, eventId: string) => void;
  findTranscriptByEvent: (eventId: string) => Transcript | undefined;
  findTranscriptBySlug: (slug: string) => Transcript | undefined;
  clearActive: () => void;
  setActive: (id: string) => void;
  updateEdited: (id: string, edited: string[]) => Promise<void>;
  updateTranscript: (
    id: string,
    transcript: Partial<TranscriptFull>,
    immediate?: boolean,
    doSync?: boolean
  ) => Promise<TranscriptFull | undefined>;
  addMeeting: (id: string) => Promise<void>;
  addMinutes: (id: string, minutes: MinutesResponseData) => Promise<TranscriptFull | undefined>;
  calculateDuration: (id: string, expectExists?: boolean) => Promise<void>;
  loadSummaries: (force?: boolean, limit?: number) => Promise<TranscriptSummary[]>;
}

const createTranscripts = (): TranscriptsWritable => {
  const initialValue = {
    idMap: new Map<string, TranscriptSummary>(),
    eventMap: new Map<string, TranscriptSummary>(),
    loaded: false
  };
  const internalStore = writable<Transcripts>(initialValue);
  const { subscribe, update, set } = internalStore;
  let loadSummariesPromise: Promise<TranscriptSummary[]> | null = null;

  return {
    update,
    subscribe,
    set,
    loadSummaries: async (force = false, limit = 1000): Promise<TranscriptSummary[]> => {
      const startTime =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now();
      const currentState = get(internalStore);
      if (currentState.loaded && !force) {
        console.log('Transcript summaries already loaded');
        transcriptsView.update((view) => ({
          ...view,
          loaded: true
        }));
        return Array.from(currentState.idMap.values());
      }

      if (loadSummariesPromise) {
        return loadSummariesPromise;
      }

      loadSummariesPromise = (async () => {
        const handler = _handler ?? (await getHandlerAsync());
        try {
          const summaries = await handler.loadTranscriptSummariesFromDB(force, limit);
          console.log('[Transcripts Store] Received', summaries.length, 'summaries from handler');

          internalStore.update((state) => {
            const newIdMap = new Map<string, TranscriptSummary>(state.idMap);
            console.log('[Transcripts Store] Starting with idMap size:', state.idMap.size);

            summaries.forEach((summary) => {
              const summaryRecord = summary as Transcript;
              const normalized: TranscriptSummary =
                summaryRecord._type === 'fullTranscript'
                  ? demoteFullTranscriptToSummary(summaryRecord as TranscriptFull)
                  : ({ ...summaryRecord, _type: 'transcriptSummary' } as TranscriptSummary);
              const existing = newIdMap.get(normalized.id);
              const merged = existing ? { ...existing, ...normalized } : normalized;
              newIdMap.set(normalized.id, merged);
            });

            console.log('[Transcripts Store] After adding summaries, idMap size:', newIdMap.size);

            const newEventMap = new Map<string, TranscriptSummary>();
            newIdMap.forEach((transcript) => {
              if (transcript.eventId) {
                newEventMap.set(transcript.eventId, transcript);
              }
            });

            const newState = {
              ...state,
              idMap: newIdMap,
              eventMap: newEventMap,
              loaded: true
            };
            console.log(
              '[Transcripts Store] Returning new state with idMap size:',
              newState.idMap.size
            );
            return newState;
          });

          // Verify the update was applied
          const verifyState = get(internalStore);
          console.log(
            '[Transcripts Store] VERIFICATION: idMap size after update:',
            verifyState.idMap.size
          );
          console.log(
            '[Transcripts Store] VERIFICATION: Sample IDs:',
            Array.from(verifyState.idMap.keys()).slice(0, 10)
          );

          transcriptsView.update((view) => ({
            ...view,
            loaded: true
          }));

          const endTime =
            typeof performance !== 'undefined' && typeof performance.now === 'function'
              ? performance.now()
              : Date.now();
          console.log(
            'Loaded transcript summaries:',
            summaries.length,
            'in',
            (endTime - startTime).toFixed(2),
            'ms'
          );

          return Array.from(get(internalStore).idMap.values());
        } catch (error) {
          console.warn('Failed to load transcript summaries:', error);
          internalStore.update((state) => ({
            ...state,
            loaded: true
          }));
          transcriptsView.update((view) => ({
            ...view,
            loaded: true
          }));
          return [];
        } finally {
          loadSummariesPromise = null;
        }
      })();

      return loadSummariesPromise;
    },
    deleteFile: async (id: string) => {
      let found: Transcript | undefined;
      update((t) => {
        if (!transcriptsLoaded) {
          console.warn('Cannot delete transcript - not loaded correctly');
        }
        found = t.idMap.get(id);
        if (!found) {
          console.warn(`Cannot set file exists - cannot find file for: ${id}`);
        }
        if (!found) return t;
        const full = ensureTranscriptFull(found, 'deleteFile');
        console.log(`Setting fileExists to false for ${id} - ${full.file?.path}`);
        full.fileExists = false;
        full.deletedAt = new Date();
        full.updatedAt = new Date();
        full.isDeleted = true;

        return t;
      });
      // Ensure we persist the deletion right away.
      if (found) {
        await saveTranscriptionLocal(found.id, found);
        // Remove from search index
        await removeFromSearchIndex(id, 'deleteFile');
      }
    },
    createFromData: createFromData,

    removeTranscriptEntry: (id: string) => {
      update((t) => {
        t.idMap.delete(id);
        return t;
      });

      // Remove from search index when transcript entry is removed
      removeFromSearchIndex(id, 'removeTranscriptEntry').then();
    },

    setStatus: (id: string, status: Transcript['status']) => {
      const existingStatus = get(transcripts).idMap.get(id)?.status;
      if (existingStatus === status) {
        return;
      }
      update((t) => {
        const found = t.idMap.get(id);
        if (!found) return t;
        found.status = status;
        return t;
      });
    },

    setName: (id: string, name: string) => {
      update((t) => {
        const found = t.idMap.get(id);
        if (!found) return t;
        found.name = name;
        return t;
      });
    },

    addEventId: (id, eventId: string) => {
      update((t) => {
        const found = t.idMap.get(id);
        if (!found) return t;
        found.eventId = eventId;
        t.eventMap.set(found.eventId, found);
        return t;
      });
    },

    findTranscriptBySlug: (slug: string): Transcript | undefined => {
      // TODO: Maintain a map for faster lookup.
      for (const [_, transcript] of get(transcripts).idMap.entries()) {
        if (!transcript) continue;
        const full = ensureTranscriptFull(transcript, 'findTranscriptBySlug');
        if (full.file?.fileName === slug) {
          return full;
        }
      }
    },

    findTranscriptByEvent: (eventId: string): TranscriptSummary | undefined => {
      return get(transcripts).eventMap.get(eventId);
    },

    setActive: (id: string) => {
      let didChange = false;
      transcriptsView.update(($t) => {
        if ($t.active !== id) {
          didChange = true;
          $t.active = id;
        }
        return $t;
      });
      if (didChange) {
        transcriptOutput.set([{ isSkeleton: true }]);
      }
    },
    clearActive: () => {
      transcriptsView.update(($t) => {
        $t.active = null;
        return $t;
      });
    },
    updateTranscript,
    updateEdited: async (id: string, edited: string[]) => {
      updateTranscript(id, { editedOutput: edited }, true, true);
    },
    addMeeting: async (id: string) => {
      const transcript = await getFullTranscript(id);
      if (!transcript) return;
      const fullTranscript = transcript;
      const file = fullTranscript.file;
      if (!file) return;
      const handler = await getHandlerAsync();
      const lastModified = await handler.getFileLastModifiedDate(file.path);

      update((t) => {
        const found = t.idMap.get(id);
        if (!found) return t;
        const full = ensureTranscriptFull(found, 'addMeeting');

        if (!full.meeting) {
          full.meeting = {
            date: lastModified || new Date(),
            attendees: ['You'],
            recording: file.path,
            minutes: null
          };
          return t;
        }

        // Do not update if it was set previously
        if (full.meeting.date) return t;
        full.meeting.date = lastModified || new Date();
        updateTranscript(
          id,
          {
            meeting: full.meeting
          },
          true,
          true
        );
        return t;
      });
    },

    addMinutes: async (id: string, minutes: MinutesResponseData): Promise<TranscriptFull | undefined> => {
      console.log(`[addMinutes] Adding minutes to ${id}`);
      const full = await getFullTranscript(id);
      if (!full) return;

      // Extract summary for denormalization (truncate to 500 chars)
      const summary = minutes.summary ? minutes.summary.slice(0, 500) : undefined;
      const normalizedTitle = minutes.title?.trim();

      const meetingUpdate = full.meeting
        ? {
          ...full.meeting,
          minutes: minutes
        }
        : {
          attendees: [],
          date: new Date(),
          recording: '',
          minutes: minutes
        };

      console.log('[addMinutes] Update data:', {
        id,
        hasSummary: !!summary,
        hasMeeting: !!meetingUpdate,
        meetingKeys: Object.keys(meetingUpdate),
        hasMinutes: !!meetingUpdate.minutes
      });

      const updatePayload: Partial<TranscriptFull> = {
        summary, // Denormalized summary field
        meeting: meetingUpdate,
        data: {
          ...full.data,
          model: minutes.model ?? undefined
        }
      };

      if (normalizedTitle) {
        updatePayload.name = normalizedTitle;
      }

      const updated = await updateTranscript(id, updatePayload, true, true);
      return updated;
    },

    calculateDuration: async (id: string, expectExists = false) => {
      try {
        const transcript = get(transcripts).idMap.get(id);
        if (!transcript) return;
        const full = ensureTranscriptFull(transcript, 'calculateDuration');
        const { file } = full;
        if (!file) return;

        let duration: number | undefined = undefined;
        const handler = await getHandlerAsync();

        try {
          duration = await handler.getDuration(file);
        } catch (e) {
          console.warn('Error getting duration for file:', file, e);
          update((t) => {
            const found = t.idMap.get(id);
            if (!found) return t;
            const fullFound = ensureTranscriptFull(found, 'calculateDuration/update');
            fullFound.duration = undefined;
            fullFound.fileExists = false;
            return t;
          });
          return;
        }

        const existingDuration = get(transcripts).idMap.get(id)?.duration;
        if (existingDuration === duration) {
          return;
        }

        console.log('file duration', file, duration);
        if (isNaN(duration)) {
          if (transcript.fileExists || expectExists) {
            // Only throw an error if the file is expected to exist.
            console.warn('Audio file could not be found:', file?.fileName);
          }
          return;
        }

        update((t) => {
          const found = t.idMap.get(id);
          if (!found) return t;
          const fullFound = ensureTranscriptFull(found, 'calculateDuration/updateSuccess');
          fullFound.duration = duration;
          fullFound.fileExists = true;
          return t;
        });
      } catch (e) {
        console.warn('Got error calculating duration:', e);
      }
    }
  };
};

/**
 * Primary store for all transcripts
 */
export const transcripts = createTranscripts();

const syncFullTranscriptInStore = (transcript: TranscriptFull): TranscriptFull => {
  transcripts.update((state) => {
    state.idMap.set(transcript.id, demoteFullTranscriptToSummary(transcript));
    if (transcript.eventId) {
      state.eventMap.set(transcript.eventId, demoteFullTranscriptToSummary(transcript));
    }
    return state;
  });
  return transcript;
};

export const registerFullTranscript = (
  transcript: TranscriptFull,
  options: { syncStore?: boolean } = {}
): void => {
  cacheFullTranscript(transcript);
  if (options.syncStore) {
    syncFullTranscriptInStore(transcript);
  }
};

export const clearFullTranscriptCache = (): void => {
  fullTranscriptCache.clear();
};

const loadFullTranscriptFromDB = async (id: string): Promise<TranscriptFull> => {
  const handler = await getHandlerAsync();
  const { transcriptModel } = await handler.getDatabaseModels();
  const rows = await transcriptModel.findAllObj({
    where: { id },
    limit: 1,
    paranoid: false
  } as any);

  const record = rows?.[0];
  if (!record) {
    throw new MissingTranscriptError(id);
  }
  if (!isTranscriptFull(record)) {
    throw new Error(`[transcripts] Record ${id} is not a full transcript`);
  }

  const full = await hydrateTranscriptFromRecord(record);
  console.log('Got full transcript from DB', full);
  registerFullTranscript(full, { syncStore: true });
  return full;
};

export const getFullTranscript = async (id: string): Promise<TranscriptFull> => {
  const cached = fullTranscriptCache.get(id);
  if (cached) {
    return cached;
  }

  try {
    return await loadFullTranscriptFromDB(id);
  } catch (error) {
    const summary = get(transcripts).idMap.get(id);
    if (
      summary &&
      !isTranscriptFull(summary) &&
      error instanceof Error &&
      error.message?.includes('Handler not set')
    ) {
      const file = await MediaFile.createAutoNamed();
      return createFromData(file, undefined, summary.duration, summary.status, summary.workspaceId);
    }
    throw error;
  }
};

export const getFullTranscriptMap = async (
  ids?: string[]
): Promise<Map<string, TranscriptFull>> => {
  let targetIds = ids;
  if (!targetIds) {
    console.warn(
      '[transcripts] getFullTranscriptMap called without ids. Loading entire transcript set.'
    );
    targetIds = Array.from(get(transcripts).idMap.keys());
  }

  if (targetIds.length === 0) {
    return new Map();
  }

  const uniqueIds = Array.from(new Set(targetIds));
  const results = await Promise.all(
    uniqueIds.map(async (id) => {
      try {
        const full = await getFullTranscript(id);
        return full;
      } catch (error) {
        console.warn(`[transcripts] Failed to load full transcript ${id}:`, error);
        return undefined;
      }
    })
  );

  const map = new Map<string, TranscriptFull>();
  results.forEach((full) => {
    if (full) {
      map.set(full.id, full);
    }
  });

  return map;
};

// Clean up on HMR to prevent memory leaks
if (typeof import.meta.hot !== 'undefined') {
  import.meta.hot?.dispose(() => {
    console.log('[HMR] Cleaning up transcripts store');
    // Clear the maps to free memory
    const currentState = get(transcripts);
    currentState.idMap.clear();
    currentState.eventMap.clear();
  });
}

/**
 * The currently active transcript
 */

type DerivedTranscript = [Readable<Transcripts>, Readable<TranscriptsView>];

export const active = derived<DerivedTranscript, Transcript | null>(
  [transcripts, transcriptsView],
  ([$t, $v]): Transcript | null => {
    if (!$v.active) return null;
    return $t.idMap.get($v.active) || null;
  }
);

active.subscribe(async ($t) => {
  if (!$t?.file) {
    return;
  }
  // For the active transcript subscription, we save the entire transcript
  // This is used when the active transcript changes, so we want to save all current state
  await debouncedSaveTranscript($t.id, $t);
});

const saveTranscriptionLocal = async (
  transcriptId: string,
  partialUpdate: Partial<Transcript>,
  doSync?: boolean
): Promise<void> => {
  if (!transcriptId) {
    return;
  }
  if (!transcriptsLoaded) {
    return;
  }

  const handler = await getHandlerAsync();
  const auth = get(userAuthToken);
  if (!auth) {
    return;
  }

  try {
    const { transcriptModel } = await handler.getDatabaseModels();

    const typedPayload = partialUpdate as Transcript | undefined;
    const isSummaryPayload = typedPayload ? isTranscriptSummary(typedPayload) : false;
    const shouldRestoreMeeting =
      isSummaryPayload &&
      ((typedPayload as any)?.meeting === null || (typedPayload as any)?.meeting === undefined);

    let preservedMeeting: TranscriptFull['meeting'] | undefined;
    if (shouldRestoreMeeting) {
      try {
        const existingFull = await getFullTranscript(transcriptId);
        preservedMeeting = existingFull.meeting ?? undefined;
      } catch (loadError) {
        console.debug(
          `[saveTranscriptionLocal] Failed to load full transcript ${transcriptId} to preserve meeting`,
          loadError
        );
      }
    }

    // Prepare update data - common logic for both sync and non-sync
    const updateData: Partial<TranscriptFull> & { id: string } = {
      id: transcriptId,
      ...(partialUpdate as any)
    };

    if (shouldRestoreMeeting) {
      if (preservedMeeting) {
        updateData.meeting = preservedMeeting;
      } else {
        delete updateData.meeting;
      }
    }

    if (partialUpdate.duration !== undefined) {
      updateData.duration = partialUpdate.duration || 0;
    }

    // Debug logging to check if meeting is in the update
    console.log('[saveTranscriptionLocal] Saving transcript with data:', {
      id: transcriptId,
      hasMeeting: !!updateData.meeting,
      meetingKeys: updateData.meeting ? Object.keys(updateData.meeting) : [],
      updateKeys: Object.keys(updateData)
    });

    if (doSync) {
      // For sync: update SQLite first, then sync to cloud
      await transcriptModel.updateItem(updateData);
      await handler.syncItems({ transcript: [{ id: transcriptId }] }, auth);
    } else {
      // For non-sync: just update SQLite
      transcriptModel.updateItem(updateData).then();
    }
  } catch (error) {
    console.debug('Error saving transcript', error);
  }
};

const debouncedSaveTranscript = debounceAsync(saveTranscriptionLocal, 'transcript.save');

export const transcriptMap = derived(transcripts, ($t) => {
  return $t.idMap;
});

let myTranscriptMap: Map<string, Transcript>;
transcriptMap.subscribe((tMap) => {
  if (tMap) {
    myTranscriptMap = tMap;
  }
});

export const getTranscriptSummary = (id: string): Transcript | undefined => myTranscriptMap.get(id);

export const getTranscript = (id: string): TranscriptFull | undefined => {
  const transcript = getTranscriptSummary(id);
  if (!transcript) {
    return undefined;
  }
  return ensureTranscriptFull(transcript, 'getTranscript');
};

/**
 * derived output store holds text value formatted for export
 */
export const output = derived(active, ($active) => {
  if (!$active) return { text: '', vtt: '' };
  const full = ensureTranscriptFull($active, 'output store');
  let parsed: TranscriptOutput = [];
  try {
    parsed = getTranscriptOutput(full);
  } catch (err) {
    console.warn('Could not get transcript output', err);
    return { text: '', vtt: '' };
  }
  return {
    text: parsed.map(({ text }: { text: string }) => text).join('\n'),
    vtt: `WEBVTT - ${full.file?.fileName}` + parsed.map(({ vtt }) => vtt).join()
  };
});

/**
 * Active transcript with workspace color
 */
export const activeWithWorkspaceColor = derived([active, workspaceStore], ([$active]) => {
  if (!$active) return null;
  return {
    ...$active,
    workspaceColor: workspaceStore.getWorkspaceColor($active.workspaceId)
  };
});

export let transcriptsLoaded = false;

transcriptsView.subscribe(($v) => {
  transcriptsLoaded = $v.loaded;
});

export const waitForTranscriptsLoaded = async (): Promise<TranscriptsView> => {
  if (transcriptsLoaded) {
    return get(transcriptsView);
  }

  return new Promise((resolve) => {
    let unsubscribe: () => void = () => { };
    const doUnsubscribe = () => {
      setTimeout(() => {
        unsubscribe?.();
      }, 100);
    };
    unsubscribe = transcriptsView.subscribe(($v) => {
      if ($v.loaded) {
        doUnsubscribe();
        resolve($v);
      }
    });
  });
};

type FilterTranscriptFn = (a: Transcript) => boolean;

const transcriptsRefreshStore = writable(1);

export const refreshTranscriptsStore = () => {
  console.log('Refreshing transcripts');
  transcriptsRefreshStore.update((value) => value + 1);
};

export const filterTranscripts = (fn: FilterTranscriptFn) => {
  return derived([transcripts, transcriptsRefreshStore], ([$t, _]) => {
    return [...$t.idMap.entries()]
      .filter((kv) => {
        return fn(kv[1]);
      })
      .sort((kv1, kv2) => {
        const t1 = kv1[1];
        const t2 = kv2[1];
        const d1 = new Date(t1.date || 0);
        const d2 = new Date(t2.date || 0);
        // @ts-expect-error These will always be dates
        return d2 - d1;
      });
  });
};

export const recordedTranscripts = filterTranscripts(($t) => !isTranscriptDeleted($t));

/**
 * Filtered transcripts with workspace colors
 */
export const recordedTranscriptsWithWorkspaceColors = derived(
  [recordedTranscripts, workspaceStore],
  ([$recordedTranscripts, $workspaceStore]) => {
    if (!$workspaceStore) return [];
    return $recordedTranscripts.map(([id, transcript]) => [
      id,
      {
        ...transcript,
        workspaceColor: workspaceStore.getWorkspaceColor(transcript.workspaceId)
      }
    ]);
  }
);

// export const draftTranscripts = filterTranscripts((t) => !t.fileExists);

export const augmentedTasks = derived(transcripts, ($t) => {
  const taskList: FormattedTask[] = [];
  if (!$t.idMap) {
    return taskList;
  }
  const today = new Date();
  Array.from($t.idMap.values())
    .map((transcript) => ensureTranscriptFull(transcript, 'augmentedTasks'))
    .filter((t) => !isTranscriptDeleted(t) && !!t.meeting?.minutes?.actionItems)
    .forEach((t, transcriptIdx) => {
      const { meeting } = t;
      if (!meeting) {
        return;
      }
      const { minutes } = meeting;
      if (!minutes) {
        return;
      }
      const actionItems: string[] = minutes.actionItems || [];
      const date = meeting?.date ? new Date(meeting.date) : new Date();
      actionItems.forEach((item, itemIdx) => {
        const owner = meeting?.attendees.length ? meeting?.attendees[0] : 'Courtney Henry';

        const task = {
          id: `meeting-${transcriptIdx}-item-${itemIdx}`,
          description: item,
          href: '#',
          assignee: { name: owner, href: '#' },
          timeUntilDue: `in ${timeBetween(today, date)}`,
          dueDate: date.toISOString(),
          status: 'notStarted'
        };
        taskList.push(task);
      });
    });
  return taskList;
});

export const transcriptOutput = writable<DisplayedTranscriptOutput>([]);

/**
 * Get the workspace color for a transcript
 * @param transcript - The transcript to get the workspace color for
 * @returns The workspace color or null if not found
 */
export const getTranscriptWorkspaceColor = (transcript: Transcript): string | null => {
  return workspaceStore.getWorkspaceColor(transcript.workspaceId);
};

/**
 * Get the workspace color by workspaceId
 * @param workspaceId - The workspace ID to get the color for
 * @returns The workspace color or null if not found
 */
export const getWorkspaceColorById = (workspaceId?: string): string | null => {
  return workspaceStore.getWorkspaceColor(workspaceId);
};

/**
 * Derived store that provides transcripts with their workspace colors
 */
export const transcriptsWithWorkspaceColors = derived(
  [transcripts, workspaceStore],
  ([$transcripts, $workspaceStore]) => {
    if (!$workspaceStore) return [];
    return Array.from($transcripts.idMap.values()).map((transcript) => ({
      ...transcript,
      workspaceColor: workspaceStore.getWorkspaceColor(transcript.workspaceId)
    }));
  }
);

/**
 * Derived store that provides transcript summaries as a readable array for navigation components
 */
export const transcriptSummariesArray = derived(transcripts, ($transcripts) => {
  const all = Array.from($transcripts.idMap.values());
  const filtered = all.filter((summary) => !isTranscriptDeleted(summary));
  console.log(
    '[transcriptSummariesArray] Total in idMap:',
    all.length,
    'After filter:',
    filtered.length
  );
  if (all.length !== filtered.length) {
    const deleted = all.filter((s) => isTranscriptDeleted(s));
    console.log('[transcriptSummariesArray] Filtered out', deleted.length, 'deleted items');
    // console.log(
    //   '[transcriptSummariesArray] Sample deleted items:',
    //   deleted.slice(0, 10).map((s) => ({
    //     id: s.id,
    //     isDeleted: s.isDeleted,
    //     isDeletedType: typeof s.isDeleted,
    //     isDeletedTruthy: !!s.isDeleted,
    //     deletedAt: s.deletedAt
    //   }))
    // );
  }
  return filtered;
});

export const audioPlayerController = writable<any>(null);
